'use client'

import { useEffect } from 'react'
import { isNativePlatform } from '@/lib/utils/platform'

// Service worker registrar — silent update pattern.
//
// Registers `/sw.js` once per page load. No banner, no dialog, no message
// protocol. New service workers auto-activate on install (`skipWaiting: true`
// in `app/sw.ts`) and take over on the user's next natural navigation —
// matching how LinkedIn, Facebook, and Twitter handle PWA updates.
//
// Guards:
//  - SSR bail.
//  - Dev bail (Turbopack HMR fights the SW — same as PR 10).
//  - Capacitor bail via `isNativePlatform()` (covers WKWebView + Android WV UA
//    + any `Capacitor.isNativePlatform()` bridge). Consistent with the rest
//    of the codebase — `app-update-modal.tsx`, `network-status-banner.tsx`.
//  - Feature detection (`'serviceWorker' in navigator`).

export function ServiceWorkerRegistrar() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (process.env.NODE_ENV === 'development') return
    if (isNativePlatform()) {
      if (process.env.NODE_ENV !== 'production') {
        console.info('[sw] Skipping registration — Capacitor native shell detected')
      }
      // A SW must never run inside the native WebView. Builds before ~v1.65.3
      // (no reliable Capacitor UA → isNativePlatform() returned false) DID
      // register one; it then persists and serves stale precached/CacheFirst
      // chunks (fresh HTML, stale interactive content). Skipping registration
      // does NOT remove an already-installed SW — so proactively unregister any
      // lingering SW and drop its Cache Storage. Idempotent and a no-op when
      // none exists.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .getRegistrations?.()
          .then((regs) => regs.forEach((r) => void r.unregister()))
          .catch(() => {})
      }
      if (typeof caches !== 'undefined') {
        caches
          .keys()
          .then((keys) => keys.forEach((k) => void caches.delete(k)))
          .catch(() => {})
      }
      return
    }
    if (!('serviceWorker' in navigator)) return

    let disposed = false

    const onLoad = () => {
      if (disposed) return
      navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[sw] Registration failed:', err)
        }
      })
    }

    if (document.readyState === 'complete') {
      onLoad()
    } else {
      window.addEventListener('load', onLoad, { once: true })
    }

    return () => {
      disposed = true
      window.removeEventListener('load', onLoad)
    }
  }, [])

  return null
}
